import React from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { renderMessageText } from "../../utils/messageText";
import VoiceNotePlayer from "./VoiceNotePlayer";
import styles from "./MiniChat.module.css";

function friendlyTimeShort(isoDate) {
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "";
  const diff = Math.max(0, Date.now() - d.getTime());
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function isCallMessage(message) {
  return message?.type === "call" || Boolean(message?.call);
}

function getCallIconClass(call) {
  return call?.type === "audio" ? "fa-phone" : "fa-video";
}

const getMessageLinkStyle = (isMine) => ({
  color: isMine ? "rgba(255,255,255,0.96)" : "#0b57d0",
  textDecoration: "underline",
  textUnderlineOffset: 2,
  overflowWrap: "anywhere",
});

export default function MiniChatMessages({
  copiedMessageKey,
  currentUser,
  dragging,
  endRef,
  error,
  friendName,
  handleAttachmentLayout,
  handleMessageCopied,
  loading,
  messageAreaRef,
  messages
}) {
  return (
    <>
      <div ref={messageAreaRef} className={styles.messageArea}>
            {/* Drop zone overlay */}
            {dragging && (
              <div className={styles.dropOverlay}>
                <i
                  className="fa-solid fa-cloud-arrow-up"
                  style={{ fontSize: 28 }}
                />
                <span>Drop files here</span>
              </div>
            )}

            {loading ? (
              <div className={styles.loading}>Loading messages...</div>
            ) : error && messages.length === 0 ? (
              <div className={styles.loading} style={{ color: "#e53935" }}>
                {error}
              </div>
            ) : messages.length === 0 ? (
              <div className={styles.emptyChat}>Say hi to {friendName}!</div>
            ) : (
              messages.map((m) => {
                const senderId = String(
                  m?.sender?._id || m?.sender?.id || m?.sender,
                );
                const isMine =
                  senderId === String(currentUser?._id || currentUser?.id);
                const messageKey = m._id || `${senderId}-${m?.createdAt}`;
                const canCopyMessage = `${m?.content || ""}`.trim().length > 0;
                const copied = copiedMessageKey === messageKey;
                const callMessage = isCallMessage(m);

                return (
                  <div
                    key={messageKey}
                    className={`${styles.msgRow} ${
                      isMine ? styles.msgRowSent : styles.msgRowReceived
                    }`}
                  >
                    <div
                      className={`${styles.msgBubble} ${
                        isMine ? styles.bubbleSent : styles.bubbleReceived
                      }`}
                    >
                      {/* Attachments */}
                      {Array.isArray(m?.attachments) &&
                        m.attachments.length > 0 && (
                          <div style={{ marginBottom: m?.content ? 4 : 0 }}>
                            {m.attachments.map((att, idx) =>
                              att.type === "image" ? (
                                <a
                                  key={idx}
                                  href={att.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <img
                                    src={att.url}
                                    alt={att.name || ""}
                                    onLoad={handleAttachmentLayout}
                                    style={{
                                      maxWidth: "100%",
                                      borderRadius: 8,
                                      marginBottom: 2,
                                    }}
                                  />
                                </a>
                              ) : att.type === "video" ? (
                                <video
                                  key={idx}
                                  src={att.url}
                                  controls
                                  onLoadedMetadata={handleAttachmentLayout}
                                  style={{
                                    maxWidth: "100%",
                                    borderRadius: 8,
                                    marginBottom: 2,
                                  }}
                                />
                              ) : att.type === "audio" ||
                                att.type === "voice_note" ? (
                                <div key={idx} style={{ marginBottom: 4 }}>
                                  <VoiceNotePlayer
                                    src={att.url}
                                    durationMs={att.durationMs}
                                    isMine={isMine}
                                    compact
                                    label={
                                      att.type === "voice_note"
                                        ? "Voice note"
                                        : att.name || "Audio"
                                    }
                                  />
                                </div>
                              ) : (
                                <a
                                  key={idx}
                                  href={att.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    fontSize: 12,
                                    color: "inherit",
                                    textDecoration: "underline",
                                    marginBottom: 2,
                                  }}
                                >
                                  <i className="fa-solid fa-file" />
                                  <span>{att.name || "Download"}</span>
                                </a>
                              ),
                        )}
                          </div>
                        )}

                      {/* Text */}
                      {m?.content && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 8,
                          }}
                        >
                          <span
                            style={{
                              flex: 1,
                              minWidth: 0,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: callMessage ? 8 : 0,
                            }}
                          >
                            {callMessage ? (
                              <i
                                className={`fa-solid ${getCallIconClass(m?.call)}`}
                                aria-hidden="true"
                                style={{ marginTop: 2 }}
                              />
                            ) : null}
                            <span
                              style={{
                                overflowWrap: "anywhere",
                              }}
                            >
                              {renderMessageText(m.content, {
                                keyPrefix: `mini-chat-${messageKey}`,
                                linkStyle: getMessageLinkStyle(isMine),
                              })}
                            </span>
                          </span>
                          {canCopyMessage ? (
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                flexShrink: 0,
                              }}
                            >
                              {canCopyMessage ? (
                                <CopyToClipboard
                                  text={m.content}
                                  onCopy={() => handleMessageCopied(messageKey)}
                                >
                                  <button
                                    type="button"
                                    title={copied ? "Copied" : "Copy message"}
                                    aria-label={
                                      copied ? "Message copied" : "Copy message"
                                    }
                                    style={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: "50%",
                                      border: "none",
                                      padding: 0,
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      cursor: "pointer",
                                      marginTop: 1,
                                      transition:
                                        "background 0.15s ease, color 0.15s ease",
                                      background: copied
                                        ? isMine
                                          ? "rgba(255,255,255,0.22)"
                                          : "rgba(46,125,50,0.12)"
                                        : isMine
                                          ? "rgba(255,255,255,0.16)"
                                          : "rgba(0,0,0,0.08)",
                                      color: copied
                                        ? isMine
                                          ? "#fff"
                                          : "#2e7d32"
                                        : isMine
                                          ? "rgba(255,255,255,0.92)"
                                          : "#5f6368",
                                    }}
                                  >
                                    <i
                                      className={`fa-solid ${copied ? "fa-check" : "fa-copy"}`}
                                      aria-hidden="true"
                                    />
                                  </button>
                                </CopyToClipboard>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      )}

                      {/* Meta: time + ticks */}
                      {m?.createdAt && (
                        <div
                          className={`${styles.msgMeta} ${
                            isMine ? styles.msgMetaSent : styles.msgMetaReceived
                          }`}
                        >
                          <span>{friendlyTimeShort(m.createdAt)}</span>
                          {isMine && (
                            <span
                              style={{
                                fontSize: 10,
                                marginLeft: 1,
                                color:
                                  m.status === "read" ? "#53bdeb" : "inherit",
                                opacity: m.status === "read" ? 1 : 0.7,
                                letterSpacing: m.status === "sent" ? 0 : -3,
                                fontWeight: 600,
                              }}
                              title={
                                m.status === "read"
                                  ? "Read"
                                  : m.status === "delivered"
                                    ? "Delivered"
                                    : "Sent"
                              }
                            >
                              {m.status === "sent" ? "\u2713" : "\u2713\u2713"}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={endRef} />
          </div>
    </>
  );
}
